import {
  markNotificationsReadRequestSchema,
  markNotificationsReadResponseSchema,
  notificationSchema,
  type MarkNotificationsReadResponse,
  type Notification,
} from "@teamna/shared";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * The notification feed, which is the same endpoint for both sides of the app.
 *
 * A notification is addressed to a user, not to a role, so an owner's feed and a member's feed are
 * one query. What differs is which rows each area shows: the owner's Activity screen keeps the
 * business-facing keys, and a member's own reminders stay out of it even when the same account holds
 * both. `ownerFacing` below is the filter that draws that line.
 *
 * Polled rather than pushed. Web Push means a service worker, VAPID keys and a permission prompt,
 * and none of that is worth carrying while the pilot is two businesses; a minute-old count on a screen
 * an owner opens a few times a day is the same answer with none of the moving parts.
 */

const POLL_MS = 60 * 1000;

/** GET /v1/me/notifications */
export function useNotifications(enabled = true): UseQueryResult<Notification[]> {
  return useQuery({
    queryKey: queryKeys.notifications,
    queryFn: () => api.list("/me/notifications", notificationSchema, { query: { limit: 50 } }),
    enabled,
    staleTime: 30 * 1000,
    refetchInterval: POLL_MS,
  });
}

/**
 * POST /v1/me/notifications/read
 *
 * Takes the timestamp of the newest row the screen has actually rendered. Anything that lands
 * between the render and this call stays unread, which is the difference between a badge that
 * clears when you look at it and a badge that quietly eats the thing you never saw.
 */
export function useMarkNotificationsRead(): UseMutationResult<
  MarkNotificationsReadResponse,
  unknown,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (through: string) =>
      api.post(
        "/me/notifications/read",
        markNotificationsReadRequestSchema.parse({ through }),
        markNotificationsReadResponseSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

/**
 * The template keys that belong to the business rather than to the person.
 *
 * A allowlist rather than a "not these" list, so a new member-facing template added next month
 * cannot accidentally start appearing in an owner's Activity feed.
 */
const OWNER_FACING = new Set<Notification["templateKey"]>(["booking_cancelled_by_member"]);

export function ownerFacing(rows: Notification[], businessId: string | null): Notification[] {
  return rows.filter(
    (row) =>
      OWNER_FACING.has(row.templateKey) &&
      (businessId === null || row.businessId === businessId) &&
      // The outbox holds a push row and a WhatsApp row for the same event on member templates. The
      // owner keys are push only today, and this keeps the feed honest if that ever changes.
      row.channel === "push",
  );
}

export function unreadCount(rows: Notification[]): number {
  return rows.filter((row) => row.readAt === null).length;
}

/** The newest row on screen, which is how far a mark-read call is allowed to reach. */
export function newestScheduledFor(rows: Notification[]): string | null {
  return rows.reduce<string | null>(
    (newest, row) =>
      newest === null || row.scheduledFor.localeCompare(newest) > 0 ? row.scheduledFor : newest,
    null,
  );
}
